import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '~/components/system/form-field';
import { ShiftFields } from './shift-fields';
import { serverMessage } from '~/lib/api/server-error';
import { useWorkDays, useWorkDayMutations } from '~/hooks/use-available-dates';
import { DAYS_OF_WEEK, emptyWorkDay, workDayToForm, type WorkDayFormValues } from '~/lib/schemas/available-date';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	wcId: number;
	year: number;
}

/** Manage the per-weekday working hours of a work calendar. */
export function WorkDaysSheet({ open, onOpenChange, wcId, year }: Props) {
	const { t } = useTranslation();
	const { data, isLoading } = useWorkDays(open ? wcId : null);
	const { create, update, remove } = useWorkDayMutations(wcId);
	const [editing, setEditing] = useState<WorkDayFormValues | null>(null);

	const save = async () => {
		if (!editing) return;
		try {
			if (editing.id) await update.mutateAsync(editing);
			else await create.mutateAsync(editing);
			toast.success(t('common.save'));
			setEditing(null);
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	return (
		<Sheet
			open={open}
			onOpenChange={o => {
				onOpenChange(o);
				if (!o) setEditing(null);
			}}>
			<SheetContent side="right" showCloseButton className="w-full gap-0 p-0 sm:max-w-md">
				<SheetHeader className="border-b border-border p-4">
					<SheetTitle>{t('system.availableDates.workDaysTitle', { year })}</SheetTitle>
				</SheetHeader>

				<div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
					{editing ? (
						<div className="flex flex-col gap-3 rounded-md border border-border bg-surface p-3">
							<FormField label={t('system.availableDates.columns.day')}>
								<Select value={editing.day} onValueChange={v => setEditing({ ...editing, day: v as WorkDayFormValues['day'] })}>
									<SelectTrigger className="w-full" aria-label={t('system.availableDates.columns.day')}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{DAYS_OF_WEEK.map(d => (
											<SelectItem key={d} value={d}>
												{t(`system.availableDates.days.${d}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</FormField>
							<ShiftFields value={editing} onChange={patch => setEditing({ ...editing, ...patch })} />
							<div className="flex justify-end gap-2">
								<Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
									{t('common.cancel')}
								</Button>
								<Button size="sm" onClick={save} disabled={create.isPending || update.isPending}>
									{t('common.save')}
								</Button>
							</div>
						</div>
					) : (
						<Button variant="outline" size="sm" className="self-start" onClick={() => setEditing(emptyWorkDay(wcId))}>
							<Plus size={15} /> {t('system.availableDates.addDay')}
						</Button>
					)}

					{isLoading ? (
						<Skeleton className="h-40 w-full rounded-md" />
					) : (
						(data ?? []).map(d => (
							<div key={d.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
								<div className="min-w-0">
									<div className="text-sm font-medium text-foreground">
										{t(`system.availableDates.days.${d.day}`, { defaultValue: d.day })}
									</div>
									<div className="text-[11px] text-foreground-muted">
										{t(`system.availableDates.dayTypes.${d.dayType}`, { defaultValue: d.dayType })}
										{d.startTime && ` · ${d.startTime.slice(0, 5)}–${(d.endTime ?? '').slice(0, 5)}`}
									</div>
								</div>
								<div className="flex items-center gap-0.5">
									<Button variant="ghost" size="icon" aria-label={t('common.edit')} onClick={() => setEditing(workDayToForm(d))}>
										<Pencil size={14} />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										aria-label={t('common.delete')}
										onClick={() => d.id && remove.mutate(d.id, { onError: err => toast.error(serverMessage(err)) })}>
										<Trash2 size={14} className="text-danger-fg" />
									</Button>
								</div>
							</div>
						))
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
}
